import { useState, useEffect, useCallback } from "react";
import { useAuth } from "@/App";
import { useNavigate } from "react-router-dom";
import { Plus, Building2, Trash2, Shield, ShieldCheck, MapPin, ChevronRight, UserPlus } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";

const LIFECYCLE_LABELS = {
  0: "Unclaimed", 1: "Claimed", 2: "Documented", 3: "Title Verifying", 4: "Title Verified",
  5: "Compliance Pending", 6: "Compliance OK", 7: "Monetization Ready", 8: "Listed",
  9: "Negotiating", 10: "Executed", 11: "Post-Sale"
};
const LIFECYCLE_COLORS = {
  0: "#71717A", 1: "#D4AF37", 2: "#F59E0B", 3: "#F59E0B", 4: "#556B2F", 5: "#F59E0B",
  6: "#556B2F", 7: "#D4AF37", 8: "#D4AF37", 9: "#F59E0B", 10: "#556B2F", 11: "#71717A"
};

export default function MyProperties() {
  const { API } = useAuth();
  const navigate = useNavigate();
  const [properties, setProperties] = useState([]);
  const [showForm, setShowForm] = useState(false);
  const [form, setForm] = useState({ name: "", address: "", property_type: "residential", status: "legal_clear", valuation: "", area_sqft: "", notes: "" });

  const fetchProperties = useCallback(async () => {
    try {
      const res = await fetch(`${API}/properties`, { credentials: "include" });
      if (res.ok) setProperties(await res.json());
    } catch (e) { console.error(e); }
  }, [API]);

  useEffect(() => { fetchProperties(); }, [fetchProperties]);

  const handleSubmit = async () => {
    try {
      const res = await fetch(`${API}/properties`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        credentials: "include",
        body: JSON.stringify({ ...form, valuation: parseFloat(form.valuation) || 0, area_sqft: parseFloat(form.area_sqft) || 0 }),
      });
      if (res.ok) {
        setShowForm(false);
        setForm({ name: "", address: "", property_type: "residential", status: "legal_clear", valuation: "", area_sqft: "", notes: "" });
        fetchProperties();
      }
    } catch (e) { console.error(e); }
  };

  const handleDelete = async (id) => {
    try {
      await fetch(`${API}/properties/${id}`, { method: "DELETE", credentials: "include" });
      fetchProperties();
    } catch (e) { console.error(e); }
  };

  const statusColors = {
    legal_clear: { bg: "bg-[#D4AF37]/10", text: "text-[#D4AF37]", label: "Legal Clear" },
    under_litigation: { bg: "bg-[#F59E0B]/10", text: "text-[#F59E0B]", label: "Under Litigation" },
    pending_valuation: { bg: "bg-[#556B2F]/10", text: "text-[#556B2F]", label: "Pending Valuation" },
  };

  return (
    <div data-testid="my-properties-page">
      <div className="flex items-center justify-between mb-6">
        <div>
          <h2 className="font-serif text-2xl text-white">My Properties</h2>
          <p className="text-white/40 text-sm font-sans">{properties.length} properties registered</p>
        </div>
        <div className="flex gap-2">
          <Button
            data-testid="onboard-property-btn"
            onClick={() => navigate("/dashboard/properties/onboard")}
            className="bg-[#D4AF37] text-black hover:bg-[#F59E0B] rounded-sm font-sans"
          >
            <UserPlus className="w-4 h-4 mr-2" /> Onboard via KYC
          </Button>
          <Button
            data-testid="add-property-form-btn"
            onClick={() => setShowForm(true)}
            variant="outline"
            className="border-white/10 text-white/60 hover:border-[#D4AF37] hover:text-[#D4AF37] rounded-sm font-sans"
          >
            <Plus className="w-4 h-4 mr-2" /> Quick Add
          </Button>
        </div>
      </div>

      {properties.length === 0 ? (
        <div className="bg-[#11130F] border border-white/10 rounded-sm p-12 text-center">
          <Building2 className="w-12 h-12 text-white/10 mx-auto mb-4" />
          <p className="text-white/40 text-sm font-sans">No properties registered yet. Add your first property to get started.</p>
        </div>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
          {properties.map((prop) => {
            const st = statusColors[prop.status] || statusColors.legal_clear;
            const lcState = prop.lifecycle_state ?? 0;
            const lcLabel = LIFECYCLE_LABELS[lcState] || `State ${lcState}`;
            const lcColor = LIFECYCLE_COLORS[lcState] || "#71717A";
            const riskScore = prop.risk_score;
            const riskColor = riskScore == null ? "#71717A" : riskScore <= 30 ? "#556B2F" : riskScore <= 60 ? "#F59E0B" : "#ef4444";
            const badgeList = prop.badges || [];
            return (
              <div key={prop.property_id} className="bg-[#11130F] border border-white/10 rounded-sm p-5 hover:border-[#D4AF37]/40 transition-colors cursor-pointer group" onClick={() => navigate(`/dashboard/properties/${prop.property_id}`)}>
                <div className="flex items-start justify-between mb-3">
                  <div className="flex items-center gap-2">
                    <Building2 className="w-5 h-5 text-[#D4AF37]" />
                    <h4 className="font-serif text-base text-white">{prop.name}</h4>
                  </div>
                  <button data-testid={`delete-property-${prop.property_id}`} onClick={(e) => { e.stopPropagation(); handleDelete(prop.property_id); }} className="text-white/20 hover:text-red-400 transition-colors">
                    <Trash2 className="w-4 h-4" />
                  </button>
                </div>
                <p className="text-white/50 text-xs font-sans mb-2">{prop.address}</p>
                {/* Survey info */}
                {prop.survey_number && (
                  <div className="flex items-center gap-1 mb-2 text-white/30 text-[10px] font-sans">
                    <MapPin className="w-3 h-3" />
                    Survey {prop.survey_number}{prop.sub_division ? `/${prop.sub_division}` : ""} — {prop.village || ""}
                  </div>
                )}
                {/* Status + Lifecycle */}
                <div className="flex items-center gap-2 mb-3 flex-wrap">
                  <span className={`${st.bg} ${st.text} text-[10px] px-2 py-0.5 rounded-sm font-sans uppercase tracking-wider`}>{st.label}</span>
                  <span className="text-[10px] px-2 py-0.5 rounded-sm font-sans border" style={{ borderColor: lcColor + "40", color: lcColor, backgroundColor: lcColor + "10" }}>
                    [{lcState}] {lcLabel}
                  </span>
                </div>
                {/* Badges */}
                {badgeList.length > 0 && (
                  <div className="flex flex-wrap gap-1 mb-3">
                    {badgeList.slice(0, 3).map(b => (
                      <span key={b} className="text-[9px] px-1.5 py-0.5 rounded-sm bg-[#D4AF37]/10 text-[#D4AF37] font-sans">{b.replace(/_/g, " ")}</span>
                    ))}
                    {badgeList.length > 3 && <span className="text-[9px] text-white/30 font-sans">+{badgeList.length - 3}</span>}
                  </div>
                )}
                {/* Bottom row: Valuation + Risk */}
                <div className="flex items-center justify-between text-xs font-sans">
                  <span className="text-white/40">Valuation</span>
                  <span className="text-white font-medium">${prop.valuation?.toLocaleString() || 0}</span>
                </div>
                <div className="flex items-center justify-between text-xs font-sans mt-1">
                  <span className="text-white/40">Risk Score</span>
                  {riskScore != null ? (
                    <span className="font-medium" style={{ color: riskColor }}>{riskScore}/100</span>
                  ) : (
                    <span className="text-white/20">Not assessed</span>
                  )}
                </div>
                <div className="mt-3 pt-3 border-t border-white/5 flex items-center justify-between">
                  <span className="text-white/20 text-[10px] font-sans">Click to view details</span>
                  <ChevronRight className="w-4 h-4 text-white/10 group-hover:text-[#D4AF37] transition-colors" />
                </div>
              </div>
            );
          })}
        </div>
      )}

      <Dialog open={showForm} onOpenChange={setShowForm}>
        <DialogContent className="bg-[#11130F] border border-white/10 text-white max-w-md">
          <DialogHeader>
            <DialogTitle className="font-serif text-xl">Add New Property</DialogTitle>
          </DialogHeader>
          <div className="space-y-4">
            <div>
              <label className="text-white/50 text-xs font-sans mb-1 block">Property Name</label>
              <Input data-testid="property-name-input" value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} placeholder="Enter property name" className="bg-white/5 border-white/10 text-white rounded-sm" />
            </div>
            <div>
              <label className="text-white/50 text-xs font-sans mb-1 block">Address</label>
              <Input data-testid="property-address-input" value={form.address} onChange={(e) => setForm({ ...form, address: e.target.value })} placeholder="Full address" className="bg-white/5 border-white/10 text-white rounded-sm" />
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div>
                <label className="text-white/50 text-xs font-sans mb-1 block">Type</label>
                <Select value={form.property_type} onValueChange={(v) => setForm({ ...form, property_type: v })}>
                  <SelectTrigger data-testid="property-type-select" className="bg-white/5 border-white/10 text-white rounded-sm">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent className="bg-[#11130F] border-white/10">
                    <SelectItem value="residential">Residential</SelectItem>
                    <SelectItem value="commercial">Commercial</SelectItem>
                    <SelectItem value="agricultural">Agricultural</SelectItem>
                    <SelectItem value="industrial">Industrial</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div>
                <label className="text-white/50 text-xs font-sans mb-1 block">Status</label>
                <Select value={form.status} onValueChange={(v) => setForm({ ...form, status: v })}>
                  <SelectTrigger data-testid="property-status-select" className="bg-white/5 border-white/10 text-white rounded-sm">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent className="bg-[#11130F] border-white/10">
                    <SelectItem value="legal_clear">Legal Clear</SelectItem>
                    <SelectItem value="under_litigation">Under Litigation</SelectItem>
                    <SelectItem value="pending_valuation">Pending Valuation</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div>
                <label className="text-white/50 text-xs font-sans mb-1 block">Valuation ($)</label>
                <Input data-testid="property-valuation-input" type="number" value={form.valuation} onChange={(e) => setForm({ ...form, valuation: e.target.value })} placeholder="0" className="bg-white/5 border-white/10 text-white rounded-sm" />
              </div>
              <div>
                <label className="text-white/50 text-xs font-sans mb-1 block">Area (sqft)</label>
                <Input data-testid="property-area-input" type="number" value={form.area_sqft} onChange={(e) => setForm({ ...form, area_sqft: e.target.value })} placeholder="0" className="bg-white/5 border-white/10 text-white rounded-sm" />
              </div>
            </div>
            <Button data-testid="submit-property-btn" onClick={handleSubmit} className="w-full bg-[#D4AF37] text-black hover:bg-[#F59E0B] rounded-sm font-sans">
              Add Property
            </Button>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}
